// Shared constants for the design studio (invitation editor).
// Data format: docs/INVITATION_DESIGN_SPEC.md

export const MAX_TEXT_FIELDS = 60;
export const MAX_ELEMENTS = 40;
export const MODE_STORAGE_KEY = 'azamein.studio.mode';

// ---- UI class strings (site design language) ----
export const EYEBROW = 'text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#6B665C]';
export const PANEL_TITLE = 'font-serif text-[22px] font-semibold text-ink';
export const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/50 focus-visible:ring-offset-1';
export const PILL_INK = `inline-flex items-center justify-center gap-2 rounded-full bg-ink text-paper font-bold hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_RING}`;
export const PILL_OUTLINE = `inline-flex items-center justify-center gap-2 rounded-full gradient-border text-ink font-bold hover:[--gb-bg:#F2F0EB] disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_RING}`;
export const GHOST_BTN = `inline-flex items-center justify-center gap-1.5 rounded-full text-ink font-bold hover:bg-cream disabled:opacity-40 disabled:hover:bg-transparent ${FOCUS_RING}`;
export const TEXT_INPUT =
  'w-full rounded-xl bg-white border border-[#DCD6C8] px-3 text-ink shadow-none outline-none focus-visible:border-transparent focus-visible:[background:linear-gradient(#fff,#fff)_padding-box,linear-gradient(120deg,#EC008C,#0CBFF2)_border-box] focus-visible:ring-[3px] focus-visible:ring-pink/10';
export const TILE = `rounded-xl bg-cream hover:bg-[#E9E5DC] ${FOCUS_RING}`;

// ---- Text ----
export const COLOR_SWATCHES = [
  ['Ink', '#1A1A1A'],
  ['Warm ink', '#3A3122'],
  ['Gold', '#8A7322'],
  ['Pink', '#EC008C'],
  ['Navy', '#1F2A44'],
  ['White', '#FFFFFF'],
];

export const TEXT_STYLES = [
  {
    id: 'heading',
    label: 'Add a heading',
    hebrew: 'כותרת',
    className: 'text-[22px] font-extrabold',
    field: { font_size: 64, font_weight: 'bold', font_family: 'Frank Ruhl Libre' },
    text: { en: 'Heading', he: 'כותרת' },
  },
  {
    id: 'subheading',
    label: 'Add a subheading',
    hebrew: 'אונטער-כותרת',
    className: 'text-[16px] font-bold',
    field: { font_size: 36, font_weight: 'bold', font_family: 'Frank Ruhl Libre' },
    text: { en: 'Subheading', he: 'אונטער-כותרת' },
  },
  {
    id: 'body',
    label: 'Add body text',
    hebrew: 'טעקסט',
    className: 'text-[13px]',
    field: { font_size: 22, font_weight: 'normal', font_family: 'Frank Ruhl Libre', line_width: 70 },
    text: { en: 'Your text here', he: 'טעקסט' },
  },
];

const RESET_EFFECTS = {
  stroke_width: 0,
  shadow_color: null,
  bg_color: null,
  highlight_color: null,
  font_style: 'normal',
  letter_spacing: 0,
};

// Light, elegant tile behind most samples; the white-outline style sits on a dark tile.
const CREAM_TILE = { background: '#FBF7EE', border: '1px solid #E6E1D6' };
const WHITE_TILE = { background: '#FFFFFF', border: '1px solid #E6E1D6' };
const GOLD = '#8A7322';
const ORNAMENT_FONT = 'Noto Sans Symbols 2';

/**
 * "Text combinations": a sample tile plus the style patch applied to a text field.
 * Clicking with a text box selected applies `patch` to it; otherwise a new text box is
 * added with `sample` as its text at `size` (font size).
 */
export const TEXT_PRESETS = [
  {
    id: 'shalom-zachar',
    name: 'Classic gold heading',
    sample: 'שלום זכר',
    size: 72,
    tile: CREAM_TILE,
    sampleStyle: { fontFamily: "'Frank Ruhl Libre', serif", fontSize: 24, fontWeight: 900, color: GOLD },
    patch: { ...RESET_EFFECTS, font_family: 'Frank Ruhl Libre', color: GOLD, font_weight: 'bold' },
  },
  {
    id: 'kiddusha-rabba',
    name: 'Bold navy heading',
    sample: 'קידושא רבה',
    size: 68,
    tile: CREAM_TILE,
    sampleStyle: { fontFamily: "'Suez One', serif", fontSize: 22, color: '#1F2A44' },
    patch: { ...RESET_EFFECTS, font_family: 'Suez One', color: '#1F2A44', font_weight: 'normal' },
  },
  {
    id: 'vachnacht',
    name: 'Burgundy heading',
    sample: 'וואכנאכט',
    size: 70,
    tile: CREAM_TILE,
    sampleStyle: { fontFamily: "'David Libre', serif", fontSize: 24, fontWeight: 700, color: '#7A1F2B' },
    patch: { ...RESET_EFFECTS, font_family: 'David Libre', color: '#7A1F2B', font_weight: 'bold' },
  },
  {
    id: 'pidyon-haben',
    name: 'Gold heading with shadow',
    sample: 'פדיון הבן',
    size: 70,
    tile: WHITE_TILE,
    sampleStyle: {
      fontFamily: "'Frank Ruhl Libre', serif",
      fontSize: 23,
      fontWeight: 900,
      color: '#B08A2E',
      textShadow: '0 2px 3px rgba(58,49,34,0.35)',
    },
    patch: {
      ...RESET_EFFECTS,
      font_family: 'Frank Ruhl Libre',
      color: '#B08A2E',
      font_weight: 'bold',
      shadow_color: 'rgba(58,49,34,0.35)',
      shadow_blur: 6,
      shadow_x: 0,
      shadow_y: 2,
    },
  },
  {
    id: 'bar-mitzvah',
    name: 'White with gold outline (for dark backgrounds)',
    sample: 'בר מצוה',
    size: 72,
    tile: { background: '#1F2A44' },
    sampleStyle: {
      fontFamily: "'Suez One', serif",
      fontSize: 24,
      color: '#FFFFFF',
      WebkitTextStroke: '2px #C9A94A',
      paintOrder: 'stroke fill',
    },
    patch: {
      ...RESET_EFFECTS,
      font_family: 'Suez One',
      color: '#FFFFFF',
      font_weight: 'normal',
      stroke_width: 2,
      stroke_color: '#C9A94A',
    },
  },
  {
    id: 'mazel-tov',
    name: 'Elegant mazel tov',
    sample: 'מזל טוב',
    size: 54,
    tile: CREAM_TILE,
    sampleStyle: { fontFamily: "'Bellefair', serif", fontSize: 24, color: GOLD, letterSpacing: 1 },
    patch: { ...RESET_EFFECTS, font_family: 'Bellefair', color: GOLD, font_weight: 'normal', letter_spacing: 2 },
  },
  {
    id: 'bsd',
    name: 'Small top line (B"H)',
    sample: 'בס"ד',
    size: 22,
    tile: WHITE_TILE,
    sampleStyle: { fontFamily: "'Frank Ruhl Libre', serif", fontSize: 16, color: '#3A3122' },
    patch: { ...RESET_EFFECTS, font_family: 'Frank Ruhl Libre', color: '#3A3122', font_weight: 'normal' },
  },
  {
    id: 'names-line',
    name: 'Names line',
    sample: "ר' אברהם יצחק ווייס",
    size: 40,
    tile: WHITE_TILE,
    sampleStyle: { fontFamily: "'Frank Ruhl Libre', serif", fontSize: 15, fontWeight: 700, color: '#3A3122' },
    patch: { ...RESET_EFFECTS, font_family: 'Frank Ruhl Libre', color: '#3A3122', font_weight: 'bold' },
  },
  {
    id: 'parsha-line',
    name: 'Parsha / date line',
    sample: 'שבת פרשת ויצא',
    size: 30,
    tile: CREAM_TILE,
    sampleStyle: { fontFamily: "'David Libre', serif", fontSize: 17, color: '#6B5F4A' },
    patch: { ...RESET_EFFECTS, font_family: 'David Libre', color: '#6B5F4A', font_weight: 'normal' },
  },
  {
    id: 'ornament-fleurons',
    name: 'Floral divider',
    sample: '❧ ❦ ❧',
    size: 40,
    tile: CREAM_TILE,
    sampleStyle: { fontFamily: `'${ORNAMENT_FONT}', serif`, fontSize: 24, color: '#B09536' },
    patch: { ...RESET_EFFECTS, font_family: ORNAMENT_FONT, color: '#B09536', font_weight: 'normal' },
  },
  {
    id: 'ornament-stars',
    name: 'Star divider',
    sample: '✦ ✦ ✦',
    size: 28,
    tile: CREAM_TILE,
    sampleStyle: { fontFamily: `'${ORNAMENT_FONT}', serif`, fontSize: 18, color: '#B09536', letterSpacing: 4 },
    patch: { ...RESET_EFFECTS, font_family: ORNAMENT_FONT, color: '#B09536', font_weight: 'normal', letter_spacing: 8 },
  },
  {
    id: 'ornament-diamond',
    name: 'Diamond divider',
    sample: '✧ ❖ ✧',
    size: 34,
    tile: WHITE_TILE,
    sampleStyle: { fontFamily: `'${ORNAMENT_FONT}', serif`, fontSize: 22, color: GOLD },
    patch: { ...RESET_EFFECTS, font_family: ORNAMENT_FONT, color: GOLD, font_weight: 'normal' },
  },
];

// ---- Colour themes ----
export const THEMES = [
  // `tint` is the matching background tint (null = original artwork).
  { id: 'ivory', name: 'Ivory & Gold', paper: '#FBF7EE', frame: '#B09536', accent: '#8A7322', ink: '#3A3122', muted: '#6B5F4A', tint: null },
  { id: 'midnight', name: 'Midnight & Gold', paper: '#1F2A44', frame: '#D9C27A', accent: '#D9C27A', ink: '#F4EBCB', muted: '#CFC6A8', tint: '#1F2A44' },
  { id: 'sage', name: 'Sage', paper: '#EEF1EA', frame: '#4A6741', accent: '#4A6741', ink: '#2F4429', muted: '#55624F', tint: '#DDE6D6' },
  { id: 'blush', name: 'Blush & Wine', paper: '#F6ECE8', frame: '#8E4A3C', accent: '#8E4A3C', ink: '#4A2A22', muted: '#7A5A50', tint: '#F3DCD5' },
  { id: 'onyx', name: 'Onyx', paper: '#1A1A1A', frame: '#C9A94A', accent: '#C9A94A', ink: '#EDE3C2', muted: '#BDB59E', tint: '#1A1A1A' },
  { id: 'azamein', name: 'Azamein pink & blue', paper: '#FFFFFF', frame: '#0CBFF2', accent: '#EC008C', ink: '#1A1A1A', muted: '#4A4640', tint: null },
];

// ---- Elements ----
// Heights are % of page height; a square of side `w`% of the width is w * 816/1056 % tall.
const SQ = 816 / 1056;

export const SHAPES = [
  { id: 'rect', name: 'Square', keywords: 'rectangle square box shape', items: [{ type: 'rect', w: 24, h: 24 * SQ, fill: '#B09536' }], preview: { background: '#B09536' } },
  { id: 'rounded', name: 'Rounded square', keywords: 'rounded rectangle square shape', items: [{ type: 'rect', w: 24, h: 24 * SQ, fill: '#1F2A44', radius: 28 }], preview: { background: '#1F2A44', borderRadius: 9 } },
  { id: 'circle', name: 'Circle', keywords: 'circle ellipse round dot shape', items: [{ type: 'ellipse', w: 22, h: 22 * SQ, fill: '#EC008C' }], preview: { background: '#EC008C', borderRadius: '50%' } },
  { id: 'outline', name: 'Outline box', keywords: 'outline box border frame shape', items: [{ type: 'rect', w: 30, h: 20, fill: null, stroke_color: '#1A1A1A', stroke_width: 4, radius: 8 }], preview: { border: '2.5px solid #1A1A1A', borderRadius: 4 } },
];

export const LINES = [
  { id: 'solid', name: 'Solid line', keywords: 'line solid divider', items: [{ type: 'line', w: 40, stroke_color: '#B09536', stroke_width: 3 }] },
  { id: 'dashed', name: 'Dashed line', keywords: 'line dashed divider', items: [{ type: 'line', w: 40, stroke_color: '#1A1A1A', stroke_width: 3, line_style: 'dashed' }] },
  { id: 'dotted', name: 'Dotted line', keywords: 'line dotted divider dots', items: [{ type: 'line', w: 40, stroke_color: '#1A1A1A', stroke_width: 4, line_style: 'dotted' }] },
  {
    id: 'ornament',
    name: 'Ornament divider',
    keywords: 'ornament divider diamond line',
    items: [
      { type: 'line', x: 38.5, w: 19, stroke_color: '#B09536', stroke_width: 2 },
      { type: 'rect', x: 50, w: 1.6, h: 1.6 * SQ, fill: '#B09536', rotation: 45 },
      { type: 'line', x: 61.5, w: 19, stroke_color: '#B09536', stroke_width: 2 },
    ],
  },
];

export const FRAMES = [
  {
    id: 'double-gold',
    name: 'Double gold frame',
    keywords: 'frame border double gold',
    items: [
      { type: 'rect', w: 92, h: 93, fill: null, stroke_color: '#B09536', stroke_width: 2 },
      { type: 'rect', w: 89, h: 90.6, fill: null, stroke_color: '#B09536', stroke_width: 1.5 },
    ],
    preview: { border: '1.5px solid #B09536', outline: '1px solid #B09536', outlineOffset: -5 },
  },
  {
    id: 'navy-arch',
    name: 'Rounded navy frame',
    keywords: 'frame border rounded navy arch',
    items: [{ type: 'rect', w: 90, h: 92, fill: null, stroke_color: '#1F2A44', stroke_width: 3, radius: 60 }],
    preview: { border: '1.5px solid #1F2A44', borderRadius: '40px 40px 6px 6px' },
  },
  {
    id: 'pink-blue',
    name: 'Pink and blue frame',
    keywords: 'frame border pink blue azamein',
    items: [
      { type: 'rect', w: 92, h: 93, fill: null, stroke_color: '#EC008C', stroke_width: 2, radius: 10 },
      { type: 'rect', w: 90, h: 91.4, fill: null, stroke_color: '#0CBFF2', stroke_width: 2, radius: 8 },
    ],
    preview: {
      border: '1.5px solid transparent',
      background: 'linear-gradient(#F2F0EB, #F2F0EB) padding-box, linear-gradient(120deg, #EC008C, #0CBFF2) border-box',
      borderRadius: 6,
    },
  },
];

export const AI_LANGUAGES = [
  ['yi', 'Yiddish'],
  ['he', 'Hebrew'],
  ['en', 'English'],
];
